import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ArrowDown, ArrowUp, ArrowUpDown, Eye, FileText, LayoutTemplate, Loader2, RefreshCw, Rocket, Search, Share2, Star, Trash2, Undo2 } from "lucide-react";
import { Header } from "@/components/Header";
import { useOperatingSystemPreviewCache } from "@/components/operating-systems/OperatingSystemPreviewCacheContext";
import { ShareOperatingSystemDialog } from "@/components/operating-systems/ShareOperatingSystemDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { ToastAction } from "@/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/components/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { createUuid } from "@/lib/uuid";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabase-function-errors";
import {
  isAppTemplateLifecycleRecord,
  isAppTemplateConversionEligible,
  isOwnedPublicAppTemplate,
  isUserAppOrOwnedPublicTemplate,
} from "@/lib/app-template-filters";
import {
  OPERATING_SYSTEM_APP_SAFE_SELECT,
  OPERATING_SYSTEM_JOB_SAFE_SELECT,
  OPERATING_SYSTEM_RUNTIME_LIFECYCLE_SAFE_SELECT,
  OperatingSystemApp,
  OperatingSystemJob,
  OperatingSystemPreviewDescriptor,
  OperatingSystemRuntimeLifecycle,
  extractArtifactTitle,
  getOperatingSystemDescription,
  getOperatingSystemFeedbackLabel,
  getOperatingSystemDetailPath,
  getOperatingSystemRuntimeLifecycleFeedback,
  getOperatingSystemRestartLabel,
  isSupportManagedAppRecovery,
  getOperatingSystemRestartOperation,
  getOperatingSystemSourcePath,
  getOperatingSystemStepLabel,
  getAppStatusDescriptor,
  getAppStatusFeedback,
  type AppStatusDescriptor,
  isActiveOsJobStatus,
  isActiveOsStatus,
} from "@/lib/operating-systems";
import { rememberAppUpdateNotificationJob } from "@/lib/app-job-notifications";
import { createAppListLoadGate, hasCompleteAppLifecycleStates } from "@/lib/appListLoadGate";
import { resolveOsPermission, type OsAccessLevel, type OsDirectGrantLike } from "@/lib/os-permissions";
import {
  getAppSelectionEligibility,
  pruneSelectedAppIds,
  selectEligibleAppIds,
  toggleSelectedAppId,
} from "@/lib/operatingSystemSelection";
import {
  AppTemplateActionError,
  appTemplateProperties,
  convertAppToTemplate,
  createAppFromTemplate,
  loadAppTemplateStates,
  publishAppTemplate,
  requestTemplatePublication,
  retryTemplateApp,
  revertAppTemplate,
} from "@/lib/app-templates";

type SourceArtifact = {
  id: string;
  content: string;
  conversation_id: string;
};

type OwnerProfile = {
  user_id: string;
  email: string | null;
  name: string | null;
};

type OperatingSystemsScope = "user" | "admin";
type OperatingSystemSortKey = "name" | "owner" | "status" | "source" | "created";
type SortDirection = "asc" | "desc";
type DeleteTarget = { kind: "one"; app: OperatingSystemApp } | { kind: "many"; apps: OperatingSystemApp[] };
type TemplateActionTarget = { kind: "convert" | "revert" | "publish" | "request"; app: OperatingSystemApp };
type AppListFilter = "all" | "mine" | "shared" | "templates";

const failureStatuses = new Set(["failed", "update_failed"]);
const authBlockedStatuses = new Set(["auth_blocked", "update_auth_blocked"]);
const STARRED_OS_STORAGE_PREFIX = "applyt.starredOperatingSystems.v1";
const STARRED_OS_MIGRATION_PREFIX = "applyt.starredOperatingSystems.migratedToDb.v1";
const STAR_PROPERTY_NAME = "starred";
const userGridClass = "grid min-w-[1060px] grid-cols-[minmax(400px,1fr)_112px_160px_132px_176px] gap-3";
const adminGridClass = "grid min-w-[1126px] grid-cols-[minmax(320px,1fr)_150px_112px_150px_126px_176px] gap-3";

function templateLifecycleStatus(app: OperatingSystemApp): string | null {
  return appTemplateProperties(app.properties).template?.status ?? null;
}

function templateInstanceStatus(app: OperatingSystemApp): string | null {
  return appTemplateProperties(app.properties).template_instance?.status ?? null;
}

function isTemplateLifecycleLocked(app: OperatingSystemApp): boolean {
  return isAppTemplateLifecycleRecord(app);
}

function formatDate(value?: string | null): string {
  if (!value) return "Not yet";
  return format(new Date(value), "MMM d, yyyy h:mm a");
}

function statusBadgeClass(status: string, templateStatus?: string | null, appClass?: string): string {
  if (templateStatus === "template") return "border-violet-300 bg-violet-50 text-violet-800";
  if (templateStatus === "preparing_template" || templateStatus === "retiring_template") {
    return "border-primary/30 bg-primary/10 text-primary";
  }
  if (templateStatus === "retire_failed") return "border-destructive/30 bg-destructive/10 text-destructive";
  return appClass ?? getAppStatusDescriptor({ app: { status: status as OperatingSystemApp["status"] } }).badgeClass;
}

function permissionBadgeLabel(level: OsAccessLevel): string | null {
  if (level === "manager") return "Owner Permission";
  if (level === "editor") return "Editor";
  if (level === "reader") return "Reader";
  return null;
}

function permissionBadgeClass(level: OsAccessLevel): string {
  if (level === "manager") return "border-primary/30 bg-primary/5 text-primary text-[10px]";
  if (level === "editor") return "border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px]";
  return "border-slate-300 bg-slate-50 text-slate-700 text-[10px]";
}

function PermissionBadge({ level }: { level: OsAccessLevel }) {
  const label = permissionBadgeLabel(level);
  if (!label) return null;
  return (
    <Badge variant="outline" className={permissionBadgeClass(level)} aria-label={`Your permission: ${label}`}>
      {label}
    </Badge>
  );
}



function starredOperatingSystemsStorageKey(userId: string): string {
  return `${STARRED_OS_STORAGE_PREFIX}:${userId}`;
}

function starredOsMigrationFlagKey(userId: string): string {
  return `${STARRED_OS_MIGRATION_PREFIX}:${userId}`;
}

function readLegacyStarredOperatingSystemIds(userId: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const value = JSON.parse(window.localStorage.getItem(starredOperatingSystemsStorageKey(userId)) || "[]") as unknown;
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === "string" && item.length > 0);
  } catch {
    return [];
  }
}

async function fetchStarredOperatingSystemIds(userId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from("os_user_properties")
    .select("os_id")
    .eq("user_id", userId)
    .eq("property_name", STAR_PROPERTY_NAME);
  if (error) throw error;
  return new Set((data ?? []).map((row) => row.os_id as string));
}

async function migrateLegacyStarsToDb(userId: string): Promise<Set<string>> {
  if (typeof window === "undefined") return new Set();
  const flagKey = starredOsMigrationFlagKey(userId);
  if (window.localStorage.getItem(flagKey) === "1") return new Set();

  const legacyIds = readLegacyStarredOperatingSystemIds(userId);
  if (legacyIds.length === 0) {
    try { window.localStorage.setItem(flagKey, "1"); } catch { /* ignore */ }
    return new Set();
  }

  const rows = legacyIds.map((os_id) => ({
    user_id: userId,
    os_id,
    property_name: STAR_PROPERTY_NAME,
    value: true,
  }));
  const { error } = await supabase
    .from("os_user_properties")
    .upsert(rows, { onConflict: "user_id,os_id,property_name" });
  if (error) throw error;

  try {
    window.localStorage.removeItem(starredOperatingSystemsStorageKey(userId));
    window.localStorage.setItem(flagKey, "1");
  } catch { /* ignore */ }
  return new Set(legacyIds);
}


function RowUuid({ value }: { value: string }) {
  return (
    <div
      className="mt-1 min-w-0 break-all font-mono text-[10.5px] leading-4 text-foreground/70"
      aria-label={`UUID ${value}`}
      title={value}
    >
      {value}
    </div>
  );
}

function AppNameButton({
  name,
  detailPath,
}: {
  name: string;
  detailPath: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={detailPath}
          className="block max-w-full break-words text-left font-medium leading-5 text-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={`Open details for ${name}`}
        >
          {name}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm break-words">
        {name}
      </TooltipContent>
    </Tooltip>
  );
}

function SourceDocumentButton({
  title,
  onOpenSource,
  ariaLabel,
}: {
  title: string;
  onOpenSource: () => void;
  ariaLabel: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onOpenSource}
          className="flex max-w-full min-w-0 items-center gap-1.5 text-left text-primary hover:underline"
          aria-label={ariaLabel}
        >
          <FileText className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{title}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm break-words">
        {title}
      </TooltipContent>
    </Tooltip>
  );
}

function OperatingSystemRowShell({
  children,
  gridClass,
  prewarmOnVisible,
  previewDescriptor,
}: {
  children: ReactNode;
  gridClass: string;
  prewarmOnVisible: boolean;
  previewDescriptor: OperatingSystemPreviewDescriptor | null;
}) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const { prewarmPreview } = useOperatingSystemPreviewCache();
  const prewarm = useCallback(() => {
    prewarmPreview(previewDescriptor);
  }, [prewarmPreview, previewDescriptor]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || !previewDescriptor || !prewarmOnVisible) return undefined;

    if (!("IntersectionObserver" in window)) {
      prewarm();
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      prewarm();
      observer.disconnect();
    }, { rootMargin: "240px 0px" });

    observer.observe(row);
    return () => observer.disconnect();
  }, [prewarm, previewDescriptor, prewarmOnVisible]);

  return (
    <div
      ref={rowRef}
      className={`${gridClass} items-center px-4 py-4 text-sm`}
      onFocusCapture={prewarm}
      onMouseEnter={prewarm}
    >
      {children}
    </div>
  );
}

function MobileOperatingSystemCard({
  app,
  job,
  artifact,
  sourceTitle,
  owner,
  appDescription,
  active,
  progress,
  visibleFeedback,
  canRestartApp,
  retryLabel,
  canDeleteApp,
  isStarred,
  selectionMode,
  isSelected,
  selectionEligibility,
  statusLabel,
  statusDescriptor,
  onToggleStar,
  onToggleSelected,
  onOpenDetails,
  onOpenSource,
  onRetry,
  onDelete,
  onShare,
  canShareApp,
  templateActionLabel,
  onTemplateAction,
  canUseTemplate,
  onUseTemplate,
  canRetirePrivateTemplate,
  onRetirePrivateTemplate,
  templateActionBusy,
  retryingId,
  deleting,
  adminScope,
  ownershipBadge,
}: {
  app: OperatingSystemApp;
  job: OperatingSystemJob | null;
  artifact: SourceArtifact | undefined;
  sourceTitle: string;
  owner: OwnerProfile | undefined;
  appDescription: string | null;
  active: boolean;
  progress: number;
  visibleFeedback: string | null;
  canRestartApp: boolean;
  retryLabel: string;
  canDeleteApp: boolean;
  isStarred: boolean;
  selectionMode: boolean;
  isSelected: boolean;
  selectionEligibility: { eligible: boolean; reason: string | null };
  statusLabel: string;
  statusDescriptor: AppStatusDescriptor;
  onToggleStar: () => void;
  onToggleSelected: () => void;
  onOpenDetails: () => void;
  onOpenSource: () => void;
  onRetry: () => void;
  onDelete: () => void;
  onShare: () => void;
  canShareApp: boolean;
  templateActionLabel: string | null;
  onTemplateAction: () => void;
  canUseTemplate: boolean;
  onUseTemplate: () => void;
  canRetirePrivateTemplate: boolean;
  onRetirePrivateTemplate: () => void;
  templateActionBusy: boolean;
  retryingId: string | null;
  deleting: boolean;
  adminScope: boolean;
  ownershipBadge?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <AppNameButton name={app.name} detailPath={getOperatingSystemDetailPath(app.id)} />
          <RowUuid value={app.id} />
        </div>
        <div className="flex items-center gap-1">
          {selectionMode ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center">
                  <Checkbox checked={isSelected} disabled={!selectionEligibility.eligible} onCheckedChange={onToggleSelected} aria-label={`Select ${app.name}`} />
                </span>
              </TooltipTrigger>
              {!selectionEligibility.eligible && <TooltipContent>{selectionEligibility.reason}</TooltipContent>}
            </Tooltip>
          ) : (
            <Button type="button" variant="ghost" size="icon" className={`h-8 w-8 shrink-0 ${isStarred ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground hover:text-foreground"}`} onClick={onToggleStar} aria-label={`${isStarred ? "Unstar" : "Star"} ${app.name}`} aria-pressed={isStarred}>
              <Star className={`h-4 w-4 ${isStarred ? "fill-current" : ""}`} />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={onOpenDetails}
            aria-label={`View details for ${app.name}`}
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {appDescription && (
        <p className="line-clamp-3 w-full text-sm font-medium leading-5 text-foreground/80">{appDescription}</p>
      )}
      {ownershipBadge && <div>{ownershipBadge}</div>}
      {visibleFeedback && (
        <div className="truncate text-xs text-muted-foreground">{visibleFeedback}</div>
      )}
      {statusDescriptor.availabilityMessage && <p className="text-xs text-muted-foreground">{statusDescriptor.availabilityMessage}</p>}
      {active && !isSupportManagedAppRecovery(job) && <Progress value={progress} className="h-1.5" />}

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs text-muted-foreground mb-1">Status</div>
          <Badge variant="outline" className={statusBadgeClass(app.status, templateLifecycleStatus(app), statusDescriptor.badgeClass)}>
            {statusLabel}
          </Badge>
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Created</div>
          <div className="text-xs text-muted-foreground">{formatDate(app.created_at)}</div>
        </div>
        {adminScope && owner && (
          <div className="col-span-2">
            <div className="text-xs text-muted-foreground mb-1">Owner</div>
            <div className="truncate font-medium text-foreground">{owner.email || owner.name || "Unknown user"}</div>
            <RowUuid value={app.user_id} />
          </div>
        )}
        <div className="col-span-2">
          <div className="text-xs text-muted-foreground mb-1">Source</div>
          <SourceDocumentButton
            title={sourceTitle}
            onOpenSource={onOpenSource}
            ariaLabel={adminScope ? `Open ${sourceTitle} in Conversations` : `Open source conversation for ${sourceTitle}`}
          />
          <RowUuid value={app.source_conversation_id} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border">
        {canRestartApp && (
          <Button size="default" variant="outline" onClick={onRetry} disabled={retryingId === app.id}>
            {retryingId === app.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
            {retryLabel}
          </Button>
        )}
        {canShareApp && (
          <Button size="default" variant="outline" onClick={onShare} aria-label={`Share ${app.name}`}>
            <Share2 className="mr-1.5 h-4 w-4" />
            Share
          </Button>
        )}
        {templateActionLabel && (
          <Button size="default" variant="outline" onClick={onTemplateAction} disabled={templateActionBusy}>
            {templateActionBusy ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : templateActionLabel === "Create Template" ? (
              <LayoutTemplate className="mr-1.5 h-4 w-4" />
            ) : (
              <Undo2 className="mr-1.5 h-4 w-4" />
            )}
            {templateActionLabel}
          </Button>
        )}
        {canUseTemplate && <Button size="default" variant="outline" onClick={onUseTemplate}>Create App from Template</Button>}
        {canRetirePrivateTemplate && <Button size="default" variant="outline" onClick={onRetirePrivateTemplate}>Remove Template</Button>}
        {canDeleteApp && (
          <Button
            size="default"
            variant="ghost"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={onDelete}
            disabled={deleting}
            aria-label={`Delete ${app.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function OperatingSystems({
  scope = "user",
  embedded = false,
}: {
  scope?: OperatingSystemsScope;
  embedded?: boolean;
}) {
  const { user, isAnonymous } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const adminScope = scope === "admin";
  const gridClass = adminScope ? adminGridClass : userGridClass;
  const [apps, setApps] = useState<OperatingSystemApp[]>([]);
  const [runtimeLifecycles, setRuntimeLifecycles] = useState<OperatingSystemRuntimeLifecycle[]>([]);
  const [jobs, setJobs] = useState<OperatingSystemJob[]>([]);
  const [artifacts, setArtifacts] = useState<SourceArtifact[]>([]);
  const [ownerProfiles, setOwnerProfiles] = useState<OwnerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [lifecycleReady, setLifecycleReady] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedAppIds, setSelectedAppIds] = useState<Set<string>>(() => new Set());
  const [sort, setSort] = useState<{ key: OperatingSystemSortKey; direction: SortDirection } | null>(null);
  const [starredOsIds, setStarredOsIds] = useState<Set<string>>(() => new Set());
  const [appListFilter, setAppListFilter] = useState<AppListFilter>("mine");
  const [appSearch, setAppSearch] = useState("");
  const [directGrants, setDirectGrants] = useState<OsDirectGrantLike[]>([]);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [shareTarget, setShareTarget] = useState<OperatingSystemApp | null>(null);
  const [templateActionTarget, setTemplateActionTarget] = useState<TemplateActionTarget | null>(null);
  const [templateActionId, setTemplateActionId] = useState<string | null>(null);
  const loadGate = useRef(createAppListLoadGate());

  const loadOperatingSystems = useCallback(async (poll = false) => {
    const generation = loadGate.current.begin(poll);
    if (generation === null) return;
    const current = () => loadGate.current.isCurrent(generation);
    if (!user?.id || isAnonymous) {
      setApps([]);
      setRuntimeLifecycles([]);
      setJobs([]);
      setArtifacts([]);
      setOwnerProfiles([]);
      setDirectGrants([]);
      setIsPlatformAdmin(false);
      setLifecycleReady(false);
      setLoading(false);
      loadGate.current.finish(generation);
      return;
    }

    setLoading(true);
    setLifecycleReady(false);
    try {
      // Resolve platform admin status once per load. Admin resolves to `manager`
      // on every App through the central resolver; mirror that in the UI badge.
      const [adminResult, grantResult] = await Promise.all([
        supabase.from("profiles").select("is_admin").eq("user_id", user.id).maybeSingle(),
        supabase.from("os_user_permissions").select("os_id, access_level").eq("user_id", user.id),
      ]);
      if (!current()) return;
      const { data: adminProfile } = adminResult;
      const callerIsAdmin = adminProfile?.is_admin === true;

      let appQuery = supabase
        .from("operating_system_apps")
        .select(OPERATING_SYSTEM_APP_SAFE_SELECT)
        .neq("status", "archived")
        .order("updated_at", { ascending: false });

      // Always fetch this user's direct grants (with level) so the permission
      // badge can be derived locally without an N+1 RPC per row.
      const { data: grantRows, error: grantError } = grantResult;
      if (grantError) throw grantError;
      const grants = ((grantRows || []) as { os_id: string; access_level: OsDirectGrantLike["access_level"] }[])
        .map((r) => ({ os_id: r.os_id, user_id: user.id, access_level: r.access_level }));

      if (!adminScope) {
        // Owned Apps plus Apps shared via a direct grant in os_user_permissions.
        const sharedIds = Array.from(new Set(grants.map((g) => g.os_id)));
        const orParts = [`user_id.eq.${user.id}`];
        if (sharedIds.length > 0) orParts.push(`id.in.(${sharedIds.join(",")})`);
        appQuery = appQuery.or(orParts.join(","));
      }

      const { data: appRows, error: appError } = await appQuery;
      if (!current()) return;
      if (appError) throw appError;

      const baseApps = (appRows || []) as unknown as OperatingSystemApp[];
      // Lifecycle flags determine filters and destructive actions. Fail closed if unavailable.
      const stateByAppId = await loadAppTemplateStates(baseApps.map((app) => app.id));
      if (!current()) return;
      if (!hasCompleteAppLifecycleStates(baseApps.map((app) => app.id), stateByAppId)) {
        throw new Error("App lifecycle state is incomplete. Please refresh.");
      }
      const nextApps = baseApps.map((app) => ({
        ...app,
        properties: stateByAppId.get(app.id),
      }));
      setRuntimeLifecycles([]);
      setOwnerProfiles([]);
      setJobs([]);
      setArtifacts([]);
      setIsPlatformAdmin(callerIsAdmin);
      setDirectGrants(grants);
      setApps(nextApps);
      setLifecycleReady(true);
      const appIds = nextApps.map((app) => app.id);
      const ownerIds = Array.from(new Set(adminScope
        ? nextApps.map((app) => app.user_id)
        : nextApps.filter((app) => app.user_id !== user.id).map((app) => app.user_id)));
      const latestJobIds = nextApps.map((app) => app.latest_job_id).filter((id): id is string => !!id);
      const sourceMessageIds = nextApps
        .map((app) => app.source_message_id)
        .filter((id): id is string => !!id);
      await Promise.all([
        (async () => {
          if (!appIds.length) return;
          const { data, error } = await supabase.from("operating_system_runtime_lifecycle")
            .select(OPERATING_SYSTEM_RUNTIME_LIFECYCLE_SAFE_SELECT).in("operating_system_id", appIds);
          if (error) throw error;
          if (current()) setRuntimeLifecycles((data || []) as unknown as OperatingSystemRuntimeLifecycle[]);
        })(),
        (async () => {
          if (!ownerIds.length) return;
          const { data, error } = await supabase.from("profiles")
            .select("user_id, email, name").in("user_id", ownerIds);
          if (error) throw error;
          if (current()) setOwnerProfiles((data || []) as OwnerProfile[]);
        })(),
        (async () => {
          if (!latestJobIds.length) return;
          const { data, error } = await supabase.from("operating_system_jobs")
            .select(OPERATING_SYSTEM_JOB_SAFE_SELECT).in("id", latestJobIds);
          if (error) throw error;
          if (current()) setJobs((data || []) as unknown as OperatingSystemJob[]);
        })(),
        (async () => {
          if (!sourceMessageIds.length) return;
          // Full document text preserves exact source titles but cannot delay first rows.
          const { data, error } = await supabase.from("messages_vr")
            .select("id, content, conversation_id").in("id", sourceMessageIds);
          if (error) throw error;
          if (current()) setArtifacts((data || []) as SourceArtifact[]);
        })(),
      ]);
    } catch (error) {
      if (!current()) return;
      console.error("Failed to load operating systems:", error);
      toast({
        title: "Unable to load Apps",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      if (current()) {
        setLoading(false);
        loadGate.current.finish(generation);
      }
    }
  }, [adminScope, isAnonymous, toast, user?.id]);

  useEffect(() => {
    const gate = loadGate.current;
    void loadOperatingSystems();
    return () => { gate.invalidate(); };
  }, [loadOperatingSystems]);

  useEffect(() => {
    if (!user?.id || isAnonymous) {
      setStarredOsIds(new Set());
      return;
    }

    const userId = user.id;
    let cancelled = false;
    (async () => {
      try {
        // One-time migration of any legacy localStorage stars into the DB.
        try { await migrateLegacyStarsToDb(userId); } catch { /* ignore migration errors */ }
        const ids = await fetchStarredOperatingSystemIds(userId);
        if (!cancelled) setStarredOsIds(ids);
      } catch (error) {
        console.error("Failed to load starred operating systems", error);
      }
    })();
    return () => { cancelled = true; };
  }, [isAnonymous, user?.id]);


  const hasActiveJobs = useMemo(
    () => jobs.some((job) => isActiveOsJobStatus(job.status)) || apps.some((app) => isActiveOsStatus(app.status)),
    [apps, jobs],
  );

  useEffect(() => {
    if (!hasActiveJobs) return;
    const timer = window.setInterval(() => {
      void loadOperatingSystems(true);
    }, 10000);
    return () => window.clearInterval(timer);
  }, [hasActiveJobs, loadOperatingSystems]);

  const jobById = useMemo(() => new Map(jobs.map((job) => [job.id, job])), [jobs]);
  const artifactById = useMemo(() => new Map(artifacts.map((artifact) => [artifact.id, artifact])), [artifacts]);
  const ownerByUserId = useMemo(() => new Map(ownerProfiles.map((profile) => [profile.user_id, profile])), [ownerProfiles]);
  const runtimeLifecycleByOsId = useMemo(
    () => new Map(runtimeLifecycles.map((lifecycle) => [lifecycle.operating_system_id, lifecycle])),
    [runtimeLifecycles],
  );
  const userApps = useMemo(() => apps.filter((app) => !isAppTemplateLifecycleRecord(app)), [apps]);
  const filteredApps = useMemo(() => {
    if (adminScope) {
      const search = appSearch.trim().toLocaleLowerCase();
      if (!search) return apps;
      return apps.filter((app) => {
        const owner = ownerByUserId.get(app.user_id);
        return [app.name, owner?.name, owner?.email]
          .some((value) => value?.toLocaleLowerCase().includes(search));
      });
    }
    if (appListFilter === "all") return apps.filter((app) => isUserAppOrOwnedPublicTemplate(app, user?.id));
    if (appListFilter === "mine") return userApps.filter((app) => app.user_id === user?.id);
    if (appListFilter === "shared") return userApps.filter((app) => app.user_id !== user?.id);
    return apps.filter((app) => isOwnedPublicAppTemplate(app, user?.id));
  }, [adminScope, appListFilter, appSearch, apps, ownerByUserId, user?.id, userApps]);
  const ownedCount = useMemo(
    () => (user?.id ? userApps.filter((app) => app.user_id === user.id).length : 0),
    [userApps, user?.id],
  );
  const sharedCount = useMemo(
    () => (user?.id ? userApps.filter((app) => app.user_id !== user.id).length : 0),
    [userApps, user?.id],
  );
  const templateCount = useMemo(
    () => apps.filter((app) => isOwnedPublicAppTemplate(app, user?.id)).length,
    [apps, user?.id],
  );
  const sortedApps = useMemo(() => {
    if (!sort) return filteredApps;

    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    const getSourceTitle = (app: OperatingSystemApp) => {
      const artifact = app.source_message_id ? artifactById.get(app.source_message_id) : null;
      return artifact ? extractArtifactTitle(artifact.content, app.name) : app.name;
    };
    const getOwnerLabel = (app: OperatingSystemApp) => {
      const owner = ownerByUserId.get(app.user_id);
      return owner?.email || owner?.name || app.user_id;
    };
    const getStatusLabel = (app: OperatingSystemApp) => getAppStatusDescriptor({
      app,
      job: app.latest_job_id ? jobById.get(app.latest_job_id) || null : null,
      lifecycle: runtimeLifecycleByOsId.get(app.id) || null,
    }).label;

    return filteredApps
      .map((app, index) => ({ app, index }))
      .sort((a, b) => {
        let result = 0;

        if (sort.key === "name") {
          result = collator.compare(a.app.name, b.app.name);
        } else if (sort.key === "owner") {
          result = collator.compare(getOwnerLabel(a.app), getOwnerLabel(b.app));
        } else if (sort.key === "status") {
          result = collator.compare(getStatusLabel(a.app), getStatusLabel(b.app));
        } else if (sort.key === "source") {
          result = collator.compare(getSourceTitle(a.app), getSourceTitle(b.app));
        } else {
          result = new Date(a.app.created_at).getTime() - new Date(b.app.created_at).getTime();
        }

        if (result === 0) return a.index - b.index;
        return sort.direction === "asc" ? result : -result;
      })
      .map(({ app }) => app);
  }, [filteredApps, artifactById, ownerByUserId, jobById, runtimeLifecycleByOsId, sort]);
  const selectedApps = useMemo(
    () => apps.filter((app) => selectedAppIds.has(app.id)),
    [apps, selectedAppIds],
  );

  useEffect(() => {
    setSelectedAppIds((current) => pruneSelectedAppIds(current, apps));
  }, [apps]);
  const retryJob = async (app: OperatingSystemApp) => {
    if (retryingId) return;
    if (templateInstanceStatus(app) === "failed") {
      setRetryingId(app.id);
      try {
        await retryTemplateApp(app.id);
        toast({ title: "Preparing App", description: "The retry is now visible in My Apps." });
        await loadOperatingSystems();
      } catch (error) {
        toast({
          title: "Retry failed",
          description: error instanceof Error ? error.message : "Unable to retry this App.",
          variant: "destructive",
        });
      } finally {
        setRetryingId(null);
      }
      return;
    }
    if (!app.source_message_id) {
      toast({
        title: "Source artifact was deleted",
        description: "This App can still be opened, but it cannot be retried without its source document.",
        variant: "destructive",
      });
      return;
    }
    const latestJob = app.latest_job_id ? jobById.get(app.latest_job_id) || null : null;
    const active = isActiveOsStatus(app.status) || isActiveOsJobStatus(latestJob?.status);
    if (active && !window.confirm("Restart this App job? The current job will be cancelled and a fresh job will be queued.")) {
      return;
    }
    setRetryingId(app.id);
    try {
      const operation = getOperatingSystemRestartOperation(app);
      const artifact = artifactById.get(app.source_message_id);
      const { data, error } = await supabase.functions.invoke("start-os-job", {
        body: {
          operation,
          operatingSystemId: app.id,
          artifactMessageId: app.source_message_id,
          artifactName: artifact ? extractArtifactTitle(artifact.content, app.name) : app.name,
          retry: true,
          forceRestart: active,
        },
      });
      if (error) throw error;
      if (operation === "update" && !data?.reused) rememberAppUpdateNotificationJob(data?.job_id);
      toast({ title: operation === "update" ? "App update restarted" : "App build restarted" });
      await loadOperatingSystems();
    } catch (error) {
      const description = await getSupabaseFunctionErrorMessage(error, "Unable to retry this job.");
      toast({
        title: "Retry failed",
        description,
        variant: "destructive",
      });
    } finally {
      setRetryingId(null);
    }
  };

  const deleteOperatingSystems = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        deleted?: number;
        already_deleted?: number;
        failed?: number;
        failed_ids?: string[];
        provider_deleted?: number;
        provider_already_deleted?: number;
        provider_failed?: number;
        error?: string;
      }>("delete-operating-systems", {
        body: deleteTarget.kind === "many"
          ? { operatingSystemIds: deleteTarget.apps.map((app) => app.id) }
          : { operatingSystemId: deleteTarget.app.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const deleted = data?.deleted ?? 1;
      const failedIds = new Set(data?.failed_ids ?? []);
      if (deleteTarget.kind === "many") {
        setSelectedAppIds(failedIds);
        if (failedIds.size === 0) setSelectionMode(false);
      } else if (!failedIds.size) {
        setSelectedAppIds(new Set());
      }
      toast({
        title: deleteTarget.kind === "many" ? "Selected Apps deleted" : "App deleted",
        description: deleteTarget.kind === "many"
            ? failedIds.size > 0
              ? `${deleted} deleted; ${failedIds.size} remaining selected because cleanup failed.`
              : `${deleted} App${deleted === 1 ? "" : "s"} permanently deleted.`
          : `"${deleteTarget.app.name}" was removed from Applyt.`,
        variant: failedIds.size > 0 ? "destructive" : undefined,
      });
      setDeleteTarget(null);
      await loadOperatingSystems();
    } catch (error) {
      const description = await getSupabaseFunctionErrorMessage(
        error,
        "Unable to delete Apps.",
      );
      toast({
        title: "Delete failed",
        description,
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const toggleStarredOperatingSystem = useCallback(async (appId: string) => {
    if (!user?.id || isAnonymous) return;

    const userId = user.id;
    const wasStarred = starredOsIds.has(appId);

    // Optimistic UI update
    setStarredOsIds((current) => {
      const next = new Set(current);
      if (wasStarred) next.delete(appId); else next.add(appId);
      return next;
    });

    try {
      if (wasStarred) {
        const { error } = await supabase
          .from("os_user_properties")
          .delete()
          .eq("user_id", userId)
          .eq("os_id", appId)
          .eq("property_name", STAR_PROPERTY_NAME);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("os_user_properties")
          .upsert(
            { user_id: userId, os_id: appId, property_name: STAR_PROPERTY_NAME, value: true },
            { onConflict: "user_id,os_id,property_name" },
          );
        if (error) throw error;
      }
    } catch (error) {
      console.error("Failed to update starred App", error);
      // Revert optimistic update on failure
      setStarredOsIds((current) => {
        const next = new Set(current);
        if (wasStarred) next.add(appId); else next.delete(appId);
        return next;
      });
      toast({ title: "Could not update star", description: "Please try again.", variant: "destructive" });
    }
  }, [isAnonymous, user?.id, starredOsIds, toast]);


  const updateSort = (key: OperatingSystemSortKey) => {
    setSort((current) => {
      if (current?.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: key === "created" ? "desc" : "asc" };
    });
  };

  const runTemplateAction = async () => {
    if (!templateActionTarget || templateActionId) return;
    const { app, kind } = templateActionTarget;
    setTemplateActionId(app.id);
    try {
      if (kind === "convert") {
        const result = await convertAppToTemplate(app.id);
        if (result.status === "failed") {
          throw new Error("Template preparation failed. Your original App remains available.");
        }
        toast({
          title: "Preparing Template",
          description: "A private Template snapshot is being prepared. Your App stays available.",
          action: <ToastAction altText="View Template" onClick={() => navigate(`/applications/${result.app_id}`)}>View Template</ToastAction>,
        });
      } else if (kind === "publish") {
        await publishAppTemplate(app.id);
        toast({ title: "Template published", description: "This Template is now available in the public catalog." });
      } else if (kind === "request") {
        await requestTemplatePublication(app.id);
        toast({ title: "Submitted for review", description: "An administrator can now review and publish this Template." });
      } else {
        await revertAppTemplate(app.id);
        const snapshot = appTemplateProperties(app.properties).template?.snapshot === true;
        toast({
          title: snapshot ? "Removing Template" : "Reverting Template",
          description: snapshot
            ? "This reusable snapshot is being removed. Your original App and existing customer Apps remain."
            : "It has been removed from the public catalog and will unlock when cleanup finishes.",
        });
      }
      setTemplateActionTarget(null);
      await loadOperatingSystems();
    } catch (error) {
      const copyServiceSetupRequired = error instanceof AppTemplateActionError && [
        "copy_service_not_configured",
        "copy_service_auth_failed",
        "copy_service_configuration_invalid",
        "copy_service_unavailable",
      ].includes(error.code);
      const templatePreflightBlocked = copyServiceSetupRequired
        || (error instanceof AppTemplateActionError && error.code === "template_preview_not_configured");
      if (templatePreflightBlocked) setTemplateActionTarget(null);
      toast({
        title: kind === "convert"
          ? templatePreflightBlocked ? "Template not started" : "Template preparation failed"
          : kind === "publish" ? "Template publication failed" : kind === "request" ? "Publication request failed" : "Template removal failed",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
        action: copyServiceSetupRequired ? (
          <ToastAction
            altText="Open CopyService settings"
            onClick={() => navigate("/admin?tab=CopyService")}
          >
            Open settings
          </ToastAction>
        ) : undefined,
      });
    } finally {
      setTemplateActionId(null);
    }
  };

  const createAppFromOwnedTemplate = async (app: OperatingSystemApp) => {
    try {
      const result = await createAppFromTemplate(app.id, createUuid());
      if (result.status === "failed") {
        toast({ title: "App preparation failed", description: "Your Template remains available. Open the new App to retry.", variant: "destructive" });
      } else {
        toast({ title: result.ready ? "App created" : "Preparing App", description: "The Template is unchanged. Confirm the App is ready before removing the Template." });
      }
      navigate(`/applications/${result.app_id}`);
    } catch (error) {
      toast({ title: "App creation failed", description: error instanceof Error ? error.message : "Please try again.", variant: "destructive" });
    }
  };

  const buildAppData = (app: OperatingSystemApp) => {
    const job = app.latest_job_id ? jobById.get(app.latest_job_id) || null : null;
    const artifact = app.source_message_id ? artifactById.get(app.source_message_id) : null;
    const sourceTitle = artifact ? extractArtifactTitle(artifact.content, app.name) : app.name;
    const owner = ownerByUserId.get(app.user_id);
    const active = isActiveOsStatus(app.status) || isActiveOsJobStatus(job?.status);
    const progress = active ? job?.progress_percent ?? 5 : app.status === "ready" ? 100 : failureStatuses.has(app.status) ? 100 : 0;
    const storedFeedback = getOperatingSystemFeedbackLabel(app.latest_job_feedback);
    const failureFeedback = getOperatingSystemFeedbackLabel(job?.error_message || app.failure_reason);
    const stepFeedback = active ? getOperatingSystemStepLabel(job?.current_step) : null;
    const runtimeLifecycle = runtimeLifecycleByOsId.get(app.id) || null;
    const lifecycleFeedback = getOperatingSystemRuntimeLifecycleFeedback(runtimeLifecycle);
    const statusDescriptor = getAppStatusDescriptor({ app, job, lifecycle: runtimeLifecycle });
    const rawFeedback = authBlockedStatuses.has(app.status)
      ? storedFeedback || "Paused until an admin reconnects the builder."
      : failureFeedback || storedFeedback || stepFeedback || lifecycleFeedback;
    const feedback = getAppStatusFeedback(rawFeedback, statusDescriptor);
    const templateStatus = templateLifecycleStatus(app);
    const instanceStatus = templateInstanceStatus(app);
    const isPrivateTemplate = templateStatus === "template" && app.public_access_level === "none";
    const statusLabel = templateStatus === "preparing_template"
      ? "preparing_template"
      : templateStatus === "template"
        ? isPrivateTemplate
            ? appTemplateProperties(app.properties).template?.publication_requested_at ? "Pending Review" : "Private Template"
            : "Published Template"
        : templateStatus === "retiring_template"
          ? appTemplateProperties(app.properties).template?.snapshot
            ? "Removing Template"
            : "Reverting Template"
          : templateStatus === "retire_failed"
            ? appTemplateProperties(app.properties).template?.snapshot ? "Removal failed" : "Revert failed"
            : instanceStatus === "preparing"
              ? "Preparing"
              : instanceStatus === "failed"
                ? "Error"
                : statusDescriptor.label;
    const normalizedFeedback = feedback?.trim().toLowerCase();
    const visibleFeedback = feedback
      && normalizedFeedback !== statusLabel.trim().toLowerCase()
      && normalizedFeedback !== statusDescriptor.availabilityMessage?.trim().toLowerCase()
      && normalizedFeedback !== "app ready"
      ? feedback
      : null;
    const canManageApp = app.user_id === user.id;
    const adminActionsAvailable = adminScope && isPlatformAdmin;
    const canRecoverApp = canManageApp || adminActionsAvailable;
    const lockedTemplate = isTemplateLifecycleLocked(app);
    const canRestartApp = !isSupportManagedAppRecovery(job) && lifecycleReady && canRecoverApp && (
      instanceStatus === "failed" ||
      (!!app.source_message_id && (failureStatuses.has(app.status) || active))
    );
    const canConvertToTemplate = lifecycleReady && isAppTemplateConversionEligible(
      app,
      adminActionsAvailable,
      Boolean(artifact),
      user.id,
    );

    // Effective permission — mirrors central SQL `resolve_os_permission`:
    // owner → manager, platform admin override → manager, direct grant → level,
    // public grant (reader/editor) → level, else none. Derived locally to avoid
    // an N+1 RPC per row; backend/RLS still enforces real access.
    const localLevel = resolveOsPermission(
      user.id,
      { id: app.id, owner_user_id: app.user_id, public_access_level: app.public_access_level ?? "none" },
      directGrants,
    );
    const effectiveLevel: OsAccessLevel = lockedTemplate
      ? "reader"
      : adminActionsAvailable && localLevel !== "manager"
        ? "manager"
        : localLevel;
    const selectionEligibility = lifecycleReady
      ? getAppSelectionEligibility({ effectiveLevel, isTemplate: lockedTemplate })
      : { eligible: false, reason: "App permissions are loading." };

    return {
      job,
      artifact,
      sourceTitle,
      owner,
      appDescription: getOperatingSystemDescription(app),
      active,
      progress,
      statusLabel,
      statusDescriptor,
      visibleFeedback,
      canRestartApp,
      retryLabel: instanceStatus === "failed" ? "Retry" : getOperatingSystemRestartLabel(app),
      canDeleteApp: selectionEligibility.eligible,
      selectionEligibility,
      effectiveLevel,
      canShareApp: lifecycleReady && effectiveLevel === "manager" && !lockedTemplate,
      isTemplate: templateStatus === "template",
      canUseTemplate: lifecycleReady && templateStatus === "template" && app.status === "ready" && app.user_id === user.id,
      canRetirePrivateTemplate: lifecycleReady && templateStatus === "template"
        && appTemplateProperties(app.properties).template?.snapshot === true
        && (app.user_id === user.id || adminActionsAvailable),
      isPrivateTemplate,
      templateActionLabel: !lifecycleReady ? null : templateStatus === "template"
        ? isPrivateTemplate && adminActionsAvailable ? "Publish Template"
          : isPrivateTemplate && app.user_id === user.id
            && appTemplateProperties(app.properties).template?.snapshot === true
            && !appTemplateProperties(app.properties).template?.publication_requested_at
            ? "Submit for review"
            : !appTemplateProperties(app.properties).template?.snapshot && adminActionsAvailable ? "Revert Template" : null
        : templateStatus === "retire_failed"
          && (adminActionsAvailable || (app.user_id === user.id && appTemplateProperties(app.properties).template?.snapshot))
          ? appTemplateProperties(app.properties).template?.snapshot ? "Retry removal" : "Retry Revert"
          : canConvertToTemplate ? "Create Template" : null,
    };
  };

  const renderSortHeader = (
    key: OperatingSystemSortKey,
    label: string,
    icon?: ReactNode,
  ) => {
    const active = sort?.key === key;
    const SortIcon = active ? (sort.direction === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;

    return (
      <button
        type="button"
        className="inline-flex min-w-0 items-center gap-1.5 text-left hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={() => updateSort(key)}
        aria-label={`Sort by ${label}`}
      >
        {icon}
        <span className="truncate">{label}</span>
        <SortIcon className={`h-3.5 w-3.5 shrink-0 ${active ? "text-primary" : "text-muted-foreground/70"}`} />
      </button>
    );
  };

  const shellClassName = embedded ? "bg-background" : "min-h-screen bg-background";

  if (!user || isAnonymous) {
    return (
      <div className={shellClassName}>
        {!embedded && <Header />}
        <main className={embedded ? "py-6 text-center" : "mx-auto max-w-3xl px-4 py-16 text-center"}>
          <Rocket className="mx-auto h-10 w-10 text-primary" />
          <h1 className="mt-4 text-2xl font-semibold text-foreground">My Apps</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to build and manage Apps from your Applyt documents.</p>
          <Button className="mt-6" onClick={() => navigate("/auth")}>Sign in</Button>
        </main>
      </div>
    );
  }

  return (
    <div className={shellClassName}>
      {!embedded && <Header />}
      <main className={embedded ? "w-full" : "mx-auto max-w-6xl px-4 py-8"}>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              {adminScope ? "All Apps" : "My Apps"}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {adminScope
                ? "Generated workspaces across all users."
                : "Your Apps, Apps shared with you, and your Templates."}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
            {adminScope && (
              <div className="relative w-full sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={appSearch}
                  onChange={(event) => {
                    setAppSearch(event.target.value);
                    setSelectedAppIds(new Set());
                  }}
                  placeholder="Search App or owner"
                  aria-label="Search Apps by name or owner"
                  className="pl-9"
                />
              </div>
            )}
            <Button variant="outline" onClick={() => void loadOperatingSystems()} disabled={loading || deleting}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Refresh
            </Button>
            {!selectionMode && sortedApps.length > 0 && (
              <Button
                variant="outline"
                onClick={() => {
                  setSelectionMode(true);
                  setSelectedAppIds(new Set());
                }}
                disabled={loading || deleting}
              >
                Select Apps
              </Button>
            )}
          </div>
        </div>

        {selectionMode && sortedApps.length > 0 && (
          <div className={`sticky ${embedded ? "top-3" : "top-20 md:top-36"} z-30 mb-4 flex flex-col gap-3 rounded-lg border border-primary/30 bg-background/95 p-3 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/85 sm:flex-row sm:items-center sm:justify-between`}>
            <div className="text-sm font-medium text-foreground" aria-live="polite">
              {selectedApps.length} App{selectedApps.length === 1 ? "" : "s"} selected
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedAppIds((current) => selectEligibleAppIds(
                  current,
                  sortedApps,
                  (app) => buildAppData(app).selectionEligibility.eligible,
                ))}
              >
                Select all in view
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setSelectionMode(false); setSelectedAppIds(new Set()); }}>
                Cancel
              </Button>
              <Button size="sm" variant="destructive" disabled={!lifecycleReady || selectedApps.length === 0 || deleting} onClick={() => setDeleteTarget({ kind: "many", apps: selectedApps })}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Delete selected
              </Button>
            </div>
          </div>
        )}

        {!adminScope && apps.length > 0 && (
          <div
            className="mb-4 grid w-full grid-cols-2 gap-0.5 rounded-md border border-border bg-muted/40 p-0.5 text-xs sm:inline-flex sm:w-auto"
            role="group"
            aria-label="Filter Apps"
          >
            {([
              { key: "mine", label: `My Apps (${ownedCount})` },
              { key: "shared", label: `Shared with me (${sharedCount})` },
              { key: "templates", label: `Templates (${templateCount})` },
              { key: "all", label: `All (${ownedCount + sharedCount + templateCount})` },
            ] as { key: AppListFilter; label: string }[]).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => { setAppListFilter(t.key); setSelectionMode(false); setSelectedAppIds(new Set()); }}
                className={`min-w-0 rounded-sm px-3 py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:py-1.5 ${
                  appListFilter === t.key
                    ? "bg-background font-medium text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                aria-pressed={appListFilter === t.key}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}


        {loading && apps.length === 0 ? (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading Apps...
          </div>
        ) : apps.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center">
            <Rocket className="mx-auto h-9 w-9 text-muted-foreground" />
            <h2 className="mt-4 text-lg font-semibold text-foreground">No Apps yet</h2>
            <p className="mt-2 text-sm text-muted-foreground">Create one from a public Template or from a generated document.</p>
            <div className="mt-5 flex justify-center gap-2">
              <Button onClick={() => navigate("/templates")}>Browse Templates</Button>
              <Button variant="outline" onClick={() => navigate("/lenses")}>Create a document</Button>
            </div>
          </div>
        ) : adminScope && appSearch.trim() && sortedApps.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center">
            <Search className="mx-auto h-9 w-9 text-muted-foreground" />
            <h2 className="mt-4 text-lg font-semibold text-foreground">No matching Apps</h2>
            <p className="mt-2 text-sm text-muted-foreground">Try an App name, owner name, or owner email.</p>
          </div>
        ) : appListFilter === "templates" && sortedApps.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center">
            <LayoutTemplate className="mx-auto h-9 w-9 text-muted-foreground" />
            <h2 className="mt-4 text-lg font-semibold text-foreground">No Templates yet</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Your private and published Templates will appear here.
            </p>
          </div>
        ) : sortedApps.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center">
            <Rocket className="mx-auto h-9 w-9 text-muted-foreground" />
            <h2 className="mt-4 text-lg font-semibold text-foreground">No Apps here yet</h2>
            <p className="mt-2 text-sm text-muted-foreground">Apps in this view will appear here.</p>
          </div>
        ) : (
          <>
          <div className="hidden lg:block overflow-x-auto rounded-lg border border-border">
            <div className={`${gridClass} border-b border-border bg-muted/40 px-4 py-3 text-xs font-medium uppercase tracking-wide text-muted-foreground`}>
              {renderSortHeader("name", "Name")}
              {adminScope && renderSortHeader("owner", "Owner")}
              {renderSortHeader("status", "Status")}
              {renderSortHeader("source", "Source", <FileText className="h-3.5 w-3.5 shrink-0" />)}
              {renderSortHeader("created", "Created")}
              <span aria-hidden="true" />
            </div>
            <div className="divide-y divide-border">
              {sortedApps.map((app) => {
                const data = buildAppData(app);
                const isStarred = starredOsIds.has(app.id);

                return (
                  <OperatingSystemRowShell
                    key={app.id}
                    gridClass={gridClass}
                    previewDescriptor={null}
                    prewarmOnVisible={false}
                  >
                    <div className="flex min-w-0 items-start gap-2">
                      {selectionMode ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center">
                              <Checkbox checked={selectedAppIds.has(app.id)} disabled={!data.selectionEligibility.eligible} onCheckedChange={() => setSelectedAppIds((current) => toggleSelectedAppId(current, app.id))} aria-label={`Select ${app.name}`} />
                            </span>
                          </TooltipTrigger>
                          {!data.selectionEligibility.eligible && <TooltipContent>{data.selectionEligibility.reason}</TooltipContent>}
                        </Tooltip>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button type="button" variant="ghost" size="icon" className={`h-7 w-7 shrink-0 ${isStarred ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground hover:text-foreground"}`} onClick={() => toggleStarredOperatingSystem(app.id)} aria-label={`${isStarred ? "Unstar" : "Star"} ${app.name}`} aria-pressed={isStarred}>
                              <Star className={`h-3.5 w-3.5 ${isStarred ? "fill-current" : ""}`} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{isStarred ? "Unstar" : "Star"}</TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                            onClick={() => navigate(getOperatingSystemDetailPath(app.id))}
                            aria-label={`View details for ${app.name}`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Details</TooltipContent>
                      </Tooltip>
                      <div className="min-w-0 flex-1">
                        <AppNameButton
                          name={app.name}
                          detailPath={getOperatingSystemDetailPath(app.id)}
                        />
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {!adminScope && (
                            app.user_id === user.id ? (
                              <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary text-[10px]">My App</Badge>
                            ) : (
                              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 text-[10px]">
                                Shared with me{data.owner?.email ? ` · ${data.owner.email}` : ""}
                              </Badge>
                            )
                          )}
                          {data.isTemplate && (
                            <Badge variant="outline" className="border-violet-300 bg-violet-50 text-violet-800 text-[10px]">
                              {data.isPrivateTemplate ? "Private Template" : "Published Template"}
                            </Badge>
                          )}
                          <PermissionBadge level={data.effectiveLevel} />
                        </div>
                        <RowUuid value={app.id} />
                        {data.statusDescriptor.availabilityMessage && <p className="mt-1 text-xs text-muted-foreground">{data.statusDescriptor.availabilityMessage}</p>}
                        {data.visibleFeedback && (
                          <div className="mt-1 truncate text-xs text-muted-foreground">{data.visibleFeedback}</div>
                        )}
                        {data.active && !isSupportManagedAppRecovery(data.job) && <Progress value={data.progress} className="mt-2 h-1.5" />}
                      </div>
                    </div>
                    {adminScope && (
                      <div className="min-w-0">
                        <div className="truncate font-medium text-foreground">{data.owner?.email || data.owner?.name || "Unknown user"}</div>
                        <div className="truncate text-[11px] text-muted-foreground">{app.user_id}</div>
                      </div>
                    )}
                    <div>
                      <Badge variant="outline" className={statusBadgeClass(app.status, templateLifecycleStatus(app), data.statusDescriptor.badgeClass)}>
                        {data.statusLabel}
                      </Badge>
                    </div>
                    <div className="min-w-0">
                      <SourceDocumentButton
                        title={data.sourceTitle}
                        onOpenSource={() => navigate(getOperatingSystemSourcePath(app, adminScope ? "conversations" : "chat"))}
                        ariaLabel={adminScope ? `Open ${data.sourceTitle} in Conversations` : `Open source conversation for ${data.sourceTitle}`}
                      />
                      <RowUuid value={app.source_conversation_id} />
                    </div>
                    <div className="text-xs text-muted-foreground">{formatDate(app.created_at)}</div>
                    <div className="flex min-w-0 flex-wrap justify-end gap-2">
                      {data.canRestartApp && (
                        <Button size="sm" variant="outline" onClick={() => void retryJob(app)} disabled={retryingId === app.id}>
                          {retryingId === app.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
                          {data.retryLabel}
                        </Button>
                      )}
                      {data.canShareApp && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShareTarget(app)}
                              aria-label={`Share ${app.name}`}
                            >
                              <Share2 className="mr-1.5 h-3.5 w-3.5" />
                              Share
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Share</TooltipContent>
                        </Tooltip>
                      )}
                      {data.canUseTemplate && <Button size="sm" variant="outline" className="h-auto min-h-9 max-w-full whitespace-normal py-2 text-center" onClick={() => void createAppFromOwnedTemplate(app)}>Create App from Template</Button>}
                      {data.canRetirePrivateTemplate && <Button size="sm" variant="outline" onClick={() => setTemplateActionTarget({ kind: "revert", app })}>Remove Template</Button>}
                      {(data.templateActionLabel || failureStatuses.has(app.status)) && (
                        <div className="flex items-center justify-end gap-2">
                          {data.templateActionLabel && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setTemplateActionTarget({
                                kind: data.templateActionLabel === "Create Template" ? "convert" : data.templateActionLabel === "Publish Template" ? "publish" : data.templateActionLabel === "Submit for review" ? "request" : "revert",
                                app,
                              })}
                              disabled={templateActionId === app.id}
                            >
                              {templateActionId === app.id ? (
                                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                              ) : data.templateActionLabel === "Create Template" ? (
                                <LayoutTemplate className="mr-1.5 h-3.5 w-3.5" />
                              ) : (
                                <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                              )}
                              {data.templateActionLabel}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    {(data.appDescription || data.canDeleteApp) && (
                      <div className="col-span-full flex min-w-0 items-start gap-3">
                        {data.appDescription ? (
                          <p className="min-w-0 flex-1 line-clamp-2 text-sm font-medium leading-5 text-foreground/80">
                            {data.appDescription}
                          </p>
                        ) : (
                          <div className="flex-1" />
                        )}
                        {data.canDeleteApp && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => setDeleteTarget({ kind: "one", app })}
                                disabled={deleting}
                                aria-label={`Delete ${app.name}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    )}
                  </OperatingSystemRowShell>
                );
              })}
            </div>
          </div>
          <div className="lg:hidden space-y-3">
            {sortedApps.map((app) => {
              const data = buildAppData(app);
              const isStarred = starredOsIds.has(app.id);

              return (
                <MobileOperatingSystemCard
                  key={app.id}
                  app={app}
                  {...data}
                  isStarred={isStarred}
                  selectionMode={selectionMode}
                  isSelected={selectedAppIds.has(app.id)}
                  selectionEligibility={data.selectionEligibility}
                  onToggleStar={() => toggleStarredOperatingSystem(app.id)}
                  onToggleSelected={() => setSelectedAppIds((current) => toggleSelectedAppId(current, app.id))}
                  onOpenDetails={() => navigate(getOperatingSystemDetailPath(app.id))}
                  onOpenSource={() => navigate(getOperatingSystemSourcePath(app, adminScope ? "conversations" : "chat"))}
                  onRetry={() => void retryJob(app)}
                  onDelete={() => setDeleteTarget({ kind: "one", app })}
                  onShare={() => setShareTarget(app)}
                  onTemplateAction={() => setTemplateActionTarget({
                    kind: data.templateActionLabel === "Create Template" ? "convert" : data.templateActionLabel === "Publish Template" ? "publish" : data.templateActionLabel === "Submit for review" ? "request" : "revert",
                    app,
                  })}
                  onUseTemplate={() => void createAppFromOwnedTemplate(app)}
                  onRetirePrivateTemplate={() => setTemplateActionTarget({ kind: "revert", app })}
                  templateActionBusy={templateActionId === app.id}
                  ownershipBadge={
                    <div className="flex flex-wrap items-center gap-1">
                      {!adminScope && (
                        app.user_id === user.id ? (
                          <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary text-[10px]">My App</Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 text-[10px]">
                            Shared with me{data.owner?.email ? ` · ${data.owner.email}` : ""}
                          </Badge>
                        )
                      )}
                      {data.isTemplate && (
                        <Badge variant="outline" className="border-violet-300 bg-violet-50 text-violet-800 text-[10px]">
                          {data.isPrivateTemplate ? "Private Template" : "Published Template"}
                        </Badge>
                      )}
                      <PermissionBadge level={data.effectiveLevel} />
                    </div>
                  }
                  retryingId={retryingId}
                  deleting={deleting}
                  adminScope={adminScope}
                />
              );
            })}
          </div>
        </>
        )}
      </main>
      {templateActionTarget && (
        <AlertDialog
          open={templateActionTarget !== null}
          onOpenChange={(open) => {
            if (!open && !templateActionId) setTemplateActionTarget(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {templateActionTarget.kind === "convert" ? "Create a private Template snapshot?"
                  : templateActionTarget.kind === "publish" ? "Publish this Template?"
                  : templateActionTarget.kind === "request" ? "Submit for publication review?"
                  : appTemplateProperties(templateActionTarget.app.properties).template?.snapshot ? "Remove this Template?" : "Revert this Template to an App?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {templateActionTarget.kind === "convert"
                  ? `A private copy of the structure and data in "${templateActionTarget.app.name}" will be saved. Your original App stays available, and later edits will not change this Template.`
                  : templateActionTarget.kind === "publish"
                    ? `The structure and data in "${templateActionTarget.app.name}" will become public through previews and copies.`
                  : templateActionTarget.kind === "request"
                    ? `Ask an administrator to review "${templateActionTarget.app.name}". It stays private until an administrator publishes it.`
                  : appTemplateProperties(templateActionTarget.app.properties).template?.snapshot
                    ? `Remove "${templateActionTarget.app.name}" as a reusable Template? New copies are blocked immediately. Its archive and unused spares are cleaned up; audit history, the original App, and existing customer Apps remain. To keep an editable App, create it first and only remove this Template when your new App is ready.`
                    : `"${templateActionTarget.app.name}" will leave the public catalog immediately. Already accepted App requests will finish; the Template archive and unused spare Apps will be removed.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={templateActionId !== null}>Cancel</AlertDialogCancel>
              {templateActionTarget.kind === "revert" && appTemplateProperties(templateActionTarget.app.properties).template?.snapshot && (
                <Button type="button" variant="outline" disabled={templateActionId !== null}
                  onClick={() => {
                    const source = templateActionTarget.app;
                    setTemplateActionTarget(null);
                    void createAppFromOwnedTemplate(source);
                  }}>
                  Create App first
                </Button>
              )}
              <AlertDialogAction
                className={templateActionTarget.kind === "revert"
                  && appTemplateProperties(templateActionTarget.app.properties).template?.snapshot
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : undefined}
                disabled={templateActionId !== null}
                onClick={(event) => {
                  event.preventDefault();
                  void runTemplateAction();
                }}
              >
                {templateActionId && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {templateActionTarget.kind === "convert" ? "Create Template" : templateActionTarget.kind === "publish" ? "Publish Template" : templateActionTarget.kind === "request" ? "Submit for review" : appTemplateProperties(templateActionTarget.app.properties).template?.snapshot ? "Delete Template" : "Revert Template"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {deleteTarget !== null && (
        <AlertDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open && !deleting) setDeleteTarget(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {deleteTarget?.kind === "many" ? `Delete ${deleteTarget.apps.length} Apps?` : "Delete this App?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget?.kind === "many"
                    ? `This permanently removes ${deleteTarget.apps.length} Apps, including ${deleteTarget.apps.slice(0, 3).map((app) => `“${app.name}”`).join(", ")}${deleteTarget.apps.length > 3 ? ", and more" : ""}, plus their job history, cached runtime details, and screenshots.`
                  : `This permanently removes "${deleteTarget?.app.name ?? "this App"}", its job history, cached runtime details, and screenshots.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleting}
                onClick={(event) => {
                  event.preventDefault();
                  void deleteOperatingSystems();
                }}
              >
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {deleteTarget?.kind === "many" ? `Delete ${deleteTarget.apps.length} Apps` : "Delete App"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {shareTarget && (
        <ShareOperatingSystemDialog
          open={shareTarget !== null}
          onOpenChange={(open) => {
            if (!open) setShareTarget(null);
          }}
          operatingSystemId={shareTarget.id}
        />
      )}
    </div>
  );
}
